/**
 * Toolbox roles, capabilities and personas (epic PI-SPEC-SW-E283).
 *
 * The one definition of the SpecTree Toolbox access model, shared by the
 * SpecTree API (which enforces every gate), the Toolbox pages (which hide
 * what the API would refuse) and the Toolbox app.
 *
 * - **Roles** are per tracker project (ADO project), except `admin`, which is
 *   global and derived from `User.isGlobalAdmin`: it is never stored as a role
 *   assignment and never assigned through the role API.
 * - A person may hold several roles on one project; their capabilities are
 *   the union (see `capabilitiesFor` in `matrix.ts`).
 * - Roles also carry behaviour (a **persona**): the Formulator asks a QA
 *   Tester tester questions, for example. When a person's roles carry
 *   different personas, they pick one per project.
 *
 * Imports zod-free constants only, so the formulator module can import from
 * here without a cycle.
 */
// ---------------------------------------------------------------------------
// Roles
// ---------------------------------------------------------------------------
/** Every Toolbox role, from most to least access. */
export const TOOLBOX_ROLES = ["admin", "project-manager", "product-owner", "developer", "qa-tester", "viewer"];
/**
 * The roles that can be assigned per project. `admin` is not among them: it
 * is derived from `User.isGlobalAdmin` and never stored.
 */
export const PROJECT_ROLES = ["project-manager", "product-owner", "developer", "qa-tester", "viewer"];
/** Plain-language role names for pages and messages. */
export const TOOLBOX_ROLE_LABELS = {
    admin: "Admin",
    "project-manager": "Project Manager",
    "product-owner": "Product Owner",
    developer: "Developer",
    "qa-tester": "QA Tester",
    viewer: "Viewer",
};
/** True when `value` is a Toolbox role. */
export function isToolboxRole(value) {
    return typeof value === "string" && TOOLBOX_ROLES.includes(value);
}
/** True when `value` is a role that can be assigned per project. */
export function isProjectRole(value) {
    return typeof value === "string" && PROJECT_ROLES.includes(value);
}
// ---------------------------------------------------------------------------
// Capabilities
// ---------------------------------------------------------------------------
/**
 * Every Toolbox capability, in display order:
 * - `prepare-sessions`: prepare grooming sessions (queue, batches, parallel runs);
 * - `manage-agenda`: post all briefs and manage the session agenda;
 * - `map-project`: map repositories, product areas and the meeting pattern;
 * - `groom-one`: groom one item;
 * - `post-single-brief`: post a single brief to the tracker;
 * - `formulator`: create or improve items with the ADO Formulator;
 * - `fogbugz`: use the FogBugz tools (needs the person's saved FogBugz key).
 *   Granted to Admins, Project Managers, Product Owners, Developers and QA
 *   Testers, never Viewers (epic PI-SPEC-SW-E305, ER-1662, Package A). Only
 *   the capability ships here; the saved-key half and any FogBugz tool ship
 *   with Package B (ER-1663);
 * - `fogbugz-to-ado`: bring a FogBugz case to the ADO board as a Bug (the
 *   case → ADO Bug tool). Granted to Admins and Project Managers only (epic
 *   PI-SPEC-SW-E311, ER-1666, Package E);
 * - `ask`: ask about the product (Product Q&A: questions answered from the
 *   product's code and written specs). Granted to Admins, Project Managers,
 *   Product Owners, Developers and QA Testers, never Viewers (epic
 *   PI-SPEC-SW-E310, ER-1667, Package F);
 * - `read-backlog`: see the backlog viewer;
 * - `read-briefs`: read grooming briefs;
 * - `read-grooming-progress`: see how grooming is going: the pilot metrics
 *   panel (epic PI-SPEC-SW-E293, 01-roles.md section 9.4: Admins, Project
 *   Managers, Product Owners and Viewers, never Developers or QA Testers);
 * - `present`: Present mode;
 * - `assign-roles`: assign Toolbox roles and change settings;
 * - `manage-repos`: set up the repositories a brief may read and turn
 *   grooming on or off for the project (epic PI-SPEC-SW-E288, 01-roles.md
 *   section 9.3: Admins own repositories). Admin only.
 */
export const TOOLBOX_CAPABILITIES = [
    "prepare-sessions",
    "manage-agenda",
    "map-project",
    "groom-one",
    "post-single-brief",
    "formulator",
    "fogbugz",
    "fogbugz-to-ado",
    "ask",
    "read-backlog",
    "read-briefs",
    "read-grooming-progress",
    "present",
    "assign-roles",
    "manage-repos",
];
/** Plain-language capability names for pages and messages. */
export const TOOLBOX_CAPABILITY_LABELS = {
    "prepare-sessions": "Prepare grooming sessions",
    "manage-agenda": "Post all briefs and manage the session agenda",
    "map-project": "Map repositories, product areas and the meeting pattern",
    "groom-one": "Groom one item",
    "post-single-brief": "Post a single brief",
    formulator: "Create and improve ADO items with the ADO Formulator",
    fogbugz: "Use the FogBugz tools (needs your saved FogBugz key)",
    "fogbugz-to-ado": "Bring FogBugz cases to the ADO board",
    ask: "Ask about the product",
    "read-backlog": "See the backlog",
    "read-briefs": "Read briefs",
    "read-grooming-progress": "See how grooming is going",
    present: "Present mode",
    "assign-roles": "Assign roles and change settings",
    "manage-repos": "Set up repositories and turn grooming on or off",
};
/** True when `value` is a Toolbox capability. */
export function isToolboxCapability(value) {
    return typeof value === "string" && TOOLBOX_CAPABILITIES.includes(value);
}
// ---------------------------------------------------------------------------
// Personas
// ---------------------------------------------------------------------------
/**
 * The behaviours a role can give the tools: `product-owner` (the default
 * questions and briefs), `developer` and `qa-tester` (tester questions in the
 * Formulator).
 */
export const TOOLBOX_PERSONAS = ["product-owner", "developer", "qa-tester"];
/** The persona used when none was chosen or sent (and by contract-1 apps). */
export const DEFAULT_TOOLBOX_PERSONA = "product-owner";
/** Plain-language persona names for the persona picker. */
export const TOOLBOX_PERSONA_LABELS = {
    "product-owner": "Product Owner",
    developer: "Developer",
    "qa-tester": "QA Tester",
};
/** True when `value` is a Toolbox persona. */
export function isToolboxPersona(value) {
    return typeof value === "string" && TOOLBOX_PERSONAS.includes(value);
}
// ---------------------------------------------------------------------------
// API error codes
// ---------------------------------------------------------------------------
/** The caller lacks the Toolbox capability the action needs on that project (HTTP 403). */
export const TOOLBOX_CAPABILITY_REQUIRED = "TOOLBOX_CAPABILITY_REQUIRED";
/** The caller may not assign or remove that role on that project (HTTP 403). */
export const TOOLBOX_ROLE_ASSIGN_FORBIDDEN = "TOOLBOX_ROLE_ASSIGN_FORBIDDEN";
/** The persona is not one the caller's roles on that project allow (HTTP 403). */
export const TOOLBOX_PERSONA_NOT_ALLOWED = "TOOLBOX_PERSONA_NOT_ALLOWED";
/** Every access error code the SpecTree API returns in `error.details.code`. */
export const TOOLBOX_ACCESS_ERROR_CODES = [
    TOOLBOX_CAPABILITY_REQUIRED,
    TOOLBOX_ROLE_ASSIGN_FORBIDDEN,
    TOOLBOX_PERSONA_NOT_ALLOWED,
];
//# sourceMappingURL=types.js.map